import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { RaceTransformData } from 'src/app/helpers/RaceHelper';
import { environment } from 'src/environments/environment';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

@Injectable({
  providedIn: 'root',
})
export class SupabaseService {
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  private readonly isBrowser: boolean;
  private supabase: SupabaseClient | null = null;
  private publicSupabase: SupabaseClient | null = null;

  constructor(private http: HttpClient, @Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      this.supabase = createClient(
        environment.SUPABASE_URL,
        environment.SUPABASE_ANON_KEY,
        {
          auth: {
            storageKey: 'rb-private-session',
            persistSession: true,
            detectSessionInUrl: false,
          },
        }
      );

      this.publicSupabase = createClient(
        environment.SUPABASE_URL,
        environment.SUPABASE_ANON_KEY,
        {
          auth: {
            storageKey: 'rb-public-session',
            persistSession: false,
            detectSessionInUrl: false,
          },
        }
      );
    }
  }

  performAction(
    actionType: 'public' | 'auth',
    action: (supabase: SupabaseClient) => any
  ): Observable<any> | any {
    if (!this.isBrowser) {
      return null;
    }

    if (actionType === 'public' && this.publicSupabase) {
      return action(this.publicSupabase);
    }

    if (actionType === 'auth' && this.supabase) {
      return action(this.supabase);
    }

    return null;
  }

  async setTokenForSupabase(): Promise<string | null> {
    if (!this.isBrowser || !this.supabase) {
      return null;
    }

    const url = environment.apiUrl + `/user/getSupabaseToken`;

    try {
      const jwtTokenResponse = await this.http
        .get<any>(url, this.httpOptions)
        .toPromise();
      const token = jwtTokenResponse?.token;

      if (!token) {
        return null;
      }

      const supabaseClient = this.supabase as any;
      supabaseClient.headers = supabaseClient.headers || {};
      supabaseClient.headers.Authorization = `Bearer ${token}`;
      supabaseClient.realtime?.setAuth?.(token);

      return token;
    } catch (error) {
      console.error('Supabase token request failed', error);
      return null;
    }
  }

  async getUserBalance(
    callback: (payload: any) => void,
    retryCount: number = 0
  ): Promise<Subscription | null> {
    if (retryCount > 3 || !this.isBrowser || !this.supabase) {
      return null;
    }

    const walletOptions = {
      event: '*',
      schema: 'public',
      table: 'wallet',
    };

    const token = await this.setTokenForSupabase();
    if (!token || !this.supabase) {
      return null;
    }

    return this.supabase
      .channel('wallet_changes')
      .on('postgres_changes', walletOptions, (payload: any) => {
        callback(payload);
      })
      .subscribe((status: any, error: any) => {
        if (status === 'SUBSCRIBED') {
          console.log('status wallet', status);
        } else {
          return this.getUserBalance(callback, retryCount + 1);
        }
      });
  }

  getRaceDetails(callback: (payload: any) => void, id: string) {
    const raceTrackSubOptions = {
      event: 'UPDATE',
      schema: 'public',
      table: 'race_track_sub',
      filter: `sub_id=eq.${id}`,
    };

    return this.performAction('public', (supabase) => {
      return supabase
        .channel('race_track_sub_changes')
        .on('postgres_changes', raceTrackSubOptions, (payload) => {
          const raceNewData = payload.new;
          const newData = RaceTransformData(raceNewData);
          callback(newData);
        })
        .subscribe((status: any) => {
          if (status === 'SUBSCRIBED') {
            console.log('status race details', status);
          } else {
            console.log('status race details', status);
          }
        });
    });
  }
}